
/**
 * Exercises Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { teamAssociationServiceMethods } from './team-association.mixin.js';

export class ExercisesService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
        Object.assign(this, teamAssociationServiceMethods());
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                pagination: response.pagination || response.meta || {},
                message: response.message || 'Exercises listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch exercises list');
        }
    }

    async getStats() {
        try {
            const response = await withRetry(() => this.api.getStats(), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return {
                success: true,
                data: payload,
                message: response?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch exercise stats');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch exercises');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Exercises oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create exercises');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Exercises güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update exercises');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Exercises silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete exercises');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                deletedCount: response.deletedCount ?? response.data?.deletedCount ?? 0,
                message: response.message || response.data?.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete exercise items');
        }
    }

    // ===================== Related BCPs =====================
    async getRelatedBcps(id, params = {}) {
        try {
            const response = await this.api.getRelatedBcps(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related BCPs fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related BCPs'); }
    }

    async addBcps(id, bcpIds) {
        try {
            const response = await this.api.addBcps(id, bcpIds);
            return { success: true, data: response.data, message: response.message || 'BCPs linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link BCPs'); }
    }

    async removeBcp(id, bcpId) {
        try {
            const response = await this.api.removeBcp(id, bcpId);
            return { success: true, data: response.data, message: response.message || 'BCP unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink BCP'); }
    }

    // ===================== Participating Vendors =====================
    async getRelatedVendors(id, params = {}) {
        try {
            const response = await this.api.getRelatedVendors(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related vendors fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related vendors'); }
    }

    async addVendors(id, vendorIds) {
        try {
            const response = await this.api.addVendors(id, vendorIds);
            return { success: true, data: response.data, message: response.message || 'Vendors linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link vendors'); }
    }

    async removeVendor(id, vendorId) {
        try {
            const response = await this.api.removeVendor(id, vendorId);
            return { success: true, data: response.data, message: response.message || 'Vendor unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink vendor'); }
    }

    // ===================== Exercise Participants =====================
    async getRelatedParticipants(id, params = {}) {
        try {
            const response = await this.api.getRelatedParticipants(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related participants fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related participants'); }
    }

    async addParticipants(id, participantIds) {
        try {
            const response = await this.api.addParticipants(id, participantIds);
            return { success: true, data: response.data, message: response.message || 'Participants linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link participants'); }
    }

    async removeParticipant(id, participantId) {
        try {
            const response = await this.api.removeParticipant(id, participantId);
            return { success: true, data: response.data, message: response.message || 'Participant unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink participant'); }
    }

    // ===================== Related Assessments =====================
    async getRelatedAssessments(id, params = {}) {
        try {
            const response = await this.api.getRelatedAssessments(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related assessments fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related assessments'); }
    }
    async addAssessments(id, assessmentIds) {
        try {
            const response = await this.api.addAssessments(id, assessmentIds);
            return { success: true, data: response.data, message: response.message || 'Assessments linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link assessments'); }
    }
    async removeAssessment(id, assessmentId) {
        try {
            const response = await this.api.removeAssessment(id, assessmentId);
            return { success: true, data: response.data, message: response.message || 'Assessment unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink assessment'); }
    }

    // ===================== Scenario Timelines =====================
    async getRelatedTimelines(id, params = {}) {
        try {
            const response = await this.api.getRelatedTimelines(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related timelines fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related timelines'); }
    }
    async addTimelines(id, payload) {
        try {
            const response = await this.api.addTimelines(id, payload);
            return { success: true, data: response.data, message: response.message || 'Timelines linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link timelines'); }
    }
    async removeTimeline(id, timelineId) {
        try {
            const response = await this.api.removeTimeline(id, timelineId);
            return { success: true, data: response.data, message: response.message || 'Timeline unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink timeline'); }
    }

    // ===================== Recovery Activities =====================
    async getRelatedRecoveryActivities(id, params = {}) {
        try {
            const response = await this.api.getRelatedRecoveryActivities(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related recovery activities fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related recovery activities'); }
    }
    async addRecoveryActivities(id, activityIds) {
        try {
            const response = await this.api.addRecoveryActivities(id, activityIds);
            return { success: true, data: response.data, message: response.message || 'Recovery activities linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link recovery activities'); }
    }
    async removeRecoveryActivity(id, activityId) {
        try {
            const response = await this.api.removeRecoveryActivity(id, activityId);
            return { success: true, data: response.data, message: response.message || 'Recovery activity unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink recovery activity'); }
    }
    async reorderRecoveryActivities(id, orderedActivityIds) {
        try {
            const response = await this.api.reorderRecoveryActivities(id, orderedActivityIds);
            return { success: true, data: response.data, message: response.message || 'Recovery activities reordered successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to reorder recovery activities'); }
    }

    // ===================== Exercise Evaluations =====================
    async getRelatedEvaluations(id, params = {}) {
        try {
            const response = await this.api.getRelatedEvaluations(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related evaluations fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related evaluations'); }
    }
    async addEvaluations(id, evaluationIds) {
        try {
            const response = await this.api.addEvaluations(id, evaluationIds);
            return { success: true, data: response.data, message: response.message || 'Evaluations linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link evaluations'); }
    }
    async removeEvaluation(id, evaluationId) {
        try {
            const response = await this.api.removeEvaluation(id, evaluationId);
            return { success: true, data: response.data, message: response.message || 'Evaluation unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink evaluation'); }
    }

    // ===================== Improvement Actions =====================
    async getRelatedImprovementActions(id, params = {}) {
        try {
            const response = await this.api.getRelatedImprovementActions(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {}, message: response.message || 'Related improvement actions fetched' };
        } catch (error) { throw this._handleError(error, 'Failed to fetch related improvement actions'); }
    }
    async getLinkableImprovementActions(id, params = {}) {
        try {
            const response = await this.api.getLinkableImprovementActions(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { throw this._handleError(error, 'Failed to fetch linkable improvement actions'); }
    }
    async addImprovementActions(id, actionIds, extra = {}) {
        try {
            const response = await this.api.addImprovementActions(id, actionIds, extra);
            return { success: true, data: response.data, message: response.message || 'Improvement actions linked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to link improvement actions'); }
    }
    async unlinkImprovementActions(id, actionIds) {
        try {
            const response = await this.api.unlinkImprovementActions(id, actionIds);
            return { success: true, data: response.data, message: response.message || 'Improvement actions unlinked' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink improvement actions'); }
    }
    async removeImprovementAction(id, actionId) {
        try {
            const response = await this.api.removeImprovementAction(id, actionId);
            return { success: true, data: response.data, message: response.message || 'Improvement action unlinked successfully' };
        } catch (error) { throw this._handleError(error, 'Failed to unlink improvement action'); }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to load party template');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            return {
                success: true,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party instance');
        }
    }

    async syncParty(id, body) {
        try {
            const response = await this.api.syncParty(id, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    async getRelatedLessonsLearned(id, params = {}) {
        try {
            const response = await this.api.getRelatedLessonsLearned(id, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related lessons');
        }
    }

    async addLessonsLearned(id, lessonIds) {
        try {
            const response = await this.api.addLessonsLearned(id, lessonIds);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link lessons');
        }
    }

    async removeLessonLearned(id, lessonId) {
        try {
            const response = await this.api.removeLessonLearned(id, lessonId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink lesson');
        }
    }
}
